import { colors } from './colors';
import {
  buildDefaultChatMessageTokens,
  CHAT_PRIMARY,
} from './chatMessage';
import { derivePrimaryScale, hexToRgba } from '../utils/colorUtils';

/** App-wide brand primary (Login, Management, shared UI kit). */
export const BRAND_PRIMARY = '#4962e5';

/** Filled CTA label on chat-green surfaces (Intervene, License, Onboarding). */
export const FEATURE_PRIMARY_FOREGROUND = colors.white;

const brandScale = derivePrimaryScale(BRAND_PRIMARY, 'light');
const chatScale = derivePrimaryScale(CHAT_PRIMARY, 'light');
const brandSoftSurface = hexToRgba(BRAND_PRIMARY, 0.08);
const chatSoftSurface = hexToRgba(CHAT_PRIMARY, 0.08);

// Surfaces and backgrounds
export const surfaces = {
  background: colors.grey[50],      // App background
  surface: colors.white,             // Default surface (cards, panels)
  surfaceSubtle: colors.grey[50],    // Subtle surface (slight contrast from background)
  surfaceMuted: colors.grey[100],    // Muted surface (less prominent areas)
  surfaceElevated: colors.white,     // Elevated surface (modals, drawers)
};

// Text colors
export const text = {
  primary: colors.grey[900],         // Primary text
  secondary: colors.grey[700],       // Secondary text
  muted: colors.grey[500],           // Muted/disabled text
  inverse: colors.white,             // Text on dark backgrounds
};

// Interactive / Action colors — global brand (not chat green)
export const interactive = {
  primary: brandScale.main,
  primaryHover: brandScale.hover,
  primaryActive: brandScale.active,
  primarySoft: brandSoftSurface,
  primaryForeground: brandScale.contrastText,
  primaryBorder: brandScale.border,
  primaryMuted: brandScale.muted,
};

// Semantic status colors
export const states = {
  success: '#12b76a',
  successSoft: '#ecfdf3',
  warning: '#f79009',
  warningSoft: '#fffaeb',
  danger: '#f04438',
  dangerSoft: '#fef3f2',
  info: '#2e90fa',
  infoSoft: '#eff8ff',
};

// Border and focus ring
export const borders = {
  border: colors.grey[300],
  borderStrong: colors.grey[400],
  focusRing: hexToRgba(BRAND_PRIMARY, 0.22),
};

// Form/input tokens — clean white fields, subtle borders (admin forms)
export const inputs = {
  radius: '8px',
  background: colors.white,
  backgroundHover: colors.white,
  backgroundFocus: colors.white,
  backgroundDisabled: colors.grey[100],
  fontSize: '14px',
  controlHeight: 40,
  paddingY: 10,
  paddingX: 12,
  cursorDisabled: 'not-allowed',
  border: colors.grey[300],
};

// Sidebar-specific tokens (legacy ModernSidebar)
export const sidebar = {
  background: '#1649b8',
  backgroundEnd: '#0d3a99',
  foreground: colors.white,
  muted: 'rgba(255, 255, 255, 0.7)',
  border: 'rgba(255, 255, 255, 0.12)',
  hover: 'rgba(255, 255, 255, 0.1)',
  active: 'rgba(255, 255, 255, 0.16)',
  activeBorder: 'rgba(255, 255, 255, 0.32)',
};

// App shell tokens (sidebar chrome + header + layout surfaces)
export const appShell = {
  sidebar: {
    bg: '#0a0a0a',
    bgElevated: '#141414',
    border: 'rgba(255,255,255,0.08)',
    text: '#fafafa',
    textNav: '#a1a1aa',
    textMuted: '#71717a',
    textSection: '#52525b',
    accent: CHAT_PRIMARY,
    accentSoft: hexToRgba(CHAT_PRIMARY, 0.16),
    accentGlow: hexToRgba(CHAT_PRIMARY, 0.35),
    hover: 'rgba(255,255,255,0.04)',
    active: 'rgba(255,255,255,0.08)',
    toggleBg: colors.white,
    toggleBorder: 'rgba(0,0,0,0.06)',
    toggleIcon: '#71717a',
    badgeForeground: colors.white,
    scrollbarThumb: 'rgba(255,255,255,0.12)',
    navRadius: 8,
  },
  header: {
    background: colors.white,
    text: colors.grey[900],
    textMuted: colors.grey[500],
    border: colors.grey[200],
    borderSubtle: colors.grey[100],
    shadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    testShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  },
    shell: {
      background: colors.grey[50],
      mobileMenuButtonBg: colors.grey[50],
    },
};

const defaultChatMessage = buildDefaultChatMessageTokens(text, surfaces);

// Chat feature tokens (Intervene + future chat modules)
export const chat = {
  primary: chatScale.main,
  primaryLight: chatScale.light,
  primaryDark: chatScale.dark,
  primarySurface: chatSoftSurface,
  primaryBorder: chatScale.border,
  primaryMuted: chatScale.muted,
  primaryContrast: FEATURE_PRIMARY_FOREGROUND,
  secondary: chatSoftSurface,
  secondaryHover: hexToRgba(CHAT_PRIMARY, 0.12),
  secondaryText: chatScale.dark,
  outboundBg: CHAT_PRIMARY,
  outboundText: '#FFFFFF',
  background: defaultChatMessage.feed.background,
  paper: surfaces.surface,
  textPrimary: text.primary,
  textSecondary: text.secondary,
  textMuted: text.muted,
  divider: borders.border,
  success: CHAT_PRIMARY,
  online: CHAT_PRIMARY,
  overlay: 'rgba(255,255,255,0.7)',
  message: defaultChatMessage,
  userList: {
    surface: surfaces.surface,
    border: borders.border,
    iconColor: text.muted,
    filterTabsBackground: '#F0F2F5',
    filterControlMarginBottom: '10px',
    itemGap: '4px',
    itemBackground: surfaces.surface,
    itemHoverBackground: '#F0F2F5',
    itemSelectedBackground: '#F0F2F5',
    itemBorder: 'transparent',
    itemHoverBorder: 'transparent',
    itemSelectedBorder: 'transparent',
    itemRadius: '8px',
    itemPaddingX: '12px',
    itemPaddingY: '10px',
    itemMinHeight: '72px',
    avatarSize: '40px',
    avatarFontSize: '14px',
    avatarBackground: '#EEF2F6',
    avatarSelectedBackground: '#EEF2F6',
    avatarColor: '#0F172A',
    avatarBadgeBorder: colors.white,
    avatarGroupBackground: hexToRgba(CHAT_PRIMARY, 0.12),
    avatarGroupColor: CHAT_PRIMARY,
    userNameColor: '#101828',
    userNameFontSize: '14px',
    userNameLineHeight: '18px',
    descriptionColor: '#667085',
    descriptionFontSize: '12px',
    descriptionLineHeight: '16px',
    metaColor: '#98A2B3',
    metaFontSize: '11px',
    presenceSize: '9px',
    presenceOffset: '-1px',
    contentGap: '10px',
    contentGapMobile: '8px',
    metaGap: '6px',
    metaGapMobile: '4px',
    previewGap: '4px',
    previewMarginTop: '2px',
    expiredBadgeHeight: '22px',
    expiredBadgePaddingX: '8px',
    expiredBadgeFontSize: '11px',
  },
};

// Status tokens
export const status = {
  expired: '#C05959',
  expiredBorder: '#E7B3B3',
  expiredBg: '#FFF7F7',
  warning: '#D97706',
  warningText: '#92650A',
  warningAction: '#9A6700',
  warningSurface: '#FFF9F0',
  warningBorder: '#F5E6CC',
};

// Data table tokens (list pages, admin tables)
export const table = {
  background: colors.white,
  headerBackground: '#F9FAFB',
  headerText: colors.grey[900],
  rowBackground: colors.white,
  rowHoverBackground: '#F9FAFB',
  borderColor: colors.grey[200],
  sortIconColor: colors.grey[500],
  rowHeight: 56,
  cellPaddingX: 20,
  surfaceShadow: '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)',
};

// Attach menu tokens
export const attachMenu = {
  template: { color: chat.primary, bg: '#EEF1F8' },
  document: { color: '#7C5CBF', bg: '#F3EDFA' },
  photo: { color: '#3B82C4', bg: '#EBF4FC' },
  voice: { color: '#E07B54', bg: '#FDF0EB' },
};

// Combine all semantic tokens into single export for backward compatibility
export const semantic = {
  surfaces,
  text,
  interactive,
  states,
  borders,
  inputs,
  table,
  sidebar,
  appShell,
  chat,
  status,
  attachMenu,
};
